import React, { useState } from "react";
import { toast } from "sonner";
import { Copy, KeyRound, Share2 } from "lucide-react";
import { api, apiError, fmtDateTime, publicAppUrl, whatsappShareUrl } from "../lib/api";
import { Button } from "./ui";

/**
 * Admin action: generate a one-time password reset link for a player who asked
 * for one, then share it on WhatsApp (straight to their number if we have it) or copy it.
 */
export default function ResetLinkPanel({ memberId, name, onGenerated, compact = false }) {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/members/${memberId}/reset-link`);
      const url = `${publicAppUrl()}/reset-password?token=${encodeURIComponent(data.token)}`;
      setLink({ ...data, url });
      onGenerated?.();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(false); }
  };

  if (!link) {
    return (
      <Button
        variant={compact ? "outline" : "dark"}
        size={compact ? "sm" : "md"}
        className={compact ? "" : "w-full"}
        onClick={generate}
        disabled={busy}
        data-testid={`reset-link-generate-${memberId}`}
      >
        <KeyRound size={15} /> {busy ? "Generating…" : "Generate reset link"}
      </Button>
    );
  }

  const first = (name || "").split(" ")[0] || "there";
  const message =
    `Hi ${first}, here's your Cricso password reset link:\n${link.url}\n\n` +
    `Open it, set a new password, then log in with ${link.email}.\n` +
    `The link works once and expires ${fmtDateTime(link.expires_at)}.`;

  const share = (e) => {
    e.preventDefault();
    const win = window.open(whatsappShareUrl(message, link.mobile), "_blank");
    if (!win) copy();
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(message); toast.success("Reset message copied"); }
    catch { toast.error("Couldn't copy — select the link and copy it manually"); }
  };

  return (
    <div className="w-full space-y-2" data-testid={`reset-link-ready-${memberId}`}>
      <div className="text-xs text-ink-muted break-all bg-black/[0.03] rounded-xl p-2.5 select-all">{link.url}</div>
      <div className="grid grid-cols-2 gap-2">
        <a
          href={whatsappShareUrl(message, link.mobile)}
          target="_blank"
          rel="noopener noreferrer"
          onClick={share}
          className="h-10 rounded-2xl bg-[#25D366] text-white hover:bg-[#20BD5A] text-sm font-semibold flex items-center justify-center gap-1.5 no-underline"
          data-testid={`reset-link-whatsapp-${memberId}`}
        >
          <Share2 size={15} /> WhatsApp{link.mobile ? ` ${first}` : ""}
        </a>
        <Button variant="outline" size="sm" className="h-10" onClick={copy}>
          <Copy size={14} /> Copy
        </Button>
      </div>
      <div className="text-[11px] text-ink-muted">One-time link • expires {fmtDateTime(link.expires_at)}</div>
    </div>
  );
}
